import { z } from 'zod'

import { DEFAULT_REPORT_OUTLINE } from './reportOutline'
import {
  ReportSectionIdSchema,
  type ReportSectionDefinition,
  type ReportSectionId,
  REPORT_SECTION_CATALOG,
  SECTION_CATALOG_VERSION,
} from './sectionCatalog'

export const OUTLINE_CATALOG_VERSION = '1.0.0'

export const OUTLINE_TEMPLATE_IDS = [
  'standard',
  'comparison-first',
  'media-heavy',
  'minimal',
] as const

export const OutlineTemplateIdSchema = z.enum(OUTLINE_TEMPLATE_IDS)
export type OutlineTemplateId = z.infer<typeof OutlineTemplateIdSchema>

export const OutlineSectionSchema = z
  .object({
    id: ReportSectionIdSchema,
    title: z.string().optional(),
    intent: z.string().optional(),
  })
  .strict()

export type OutlineSection = z.infer<typeof OutlineSectionSchema>

export const OutlineTemplateSchema = z
  .object({
    id: OutlineTemplateIdSchema,
    label: z.string(),
    description: z.string().optional(),
    sectionCatalogVersion: z.string().optional(),
    sections: z.array(OutlineSectionSchema),
  })
  .strict()

export type OutlineTemplate = z.infer<typeof OutlineTemplateSchema>

const SECTION_META_BY_ID = new Map<
  string,
  Pick<ReportSectionDefinition, 'title' | 'intent'>
>(
  REPORT_SECTION_CATALOG.map((section) => [
    section.id,
    { title: section.title, intent: section.intent },
  ])
)

function resolveSection(section: OutlineSection): OutlineSection {
  const meta = SECTION_META_BY_ID.get(section.id)
  return {
    ...section,
    title: section.title ?? meta?.title,
    intent: section.intent ?? meta?.intent,
  }
}

export const OUTLINE_TEMPLATES: ReadonlyArray<OutlineTemplate> = [
  {
    id: 'standard',
    label: 'Standard',
    description: 'Full report (includes media + community insights).',
    sectionCatalogVersion: SECTION_CATALOG_VERSION,
    sections: DEFAULT_REPORT_OUTLINE.map((section) => ({
      id: section.id,
      title: section.title,
      intent: section.intent,
    })),
  },
  {
    id: 'comparison-first',
    label: 'Comparison First',
    description:
      'Surface quick picks + products early; supporting sections later.',
    sectionCatalogVersion: SECTION_CATALOG_VERSION,
    sections: [
      { id: 'introduction' },
      { id: 'quick-picks' },
      { id: 'products' },
      { id: 'what-we-looked-for' },
      { id: 'how-to-choose' },
      { id: 'evidence-snapshot' },
      { id: 'video-insights' },
      { id: 'social-insights' },
      { id: 'our-recommendation' },
      { id: 'final-recommendations' },
      { id: 'sources' },
    ],
  },
  {
    id: 'media-heavy',
    label: 'Media Heavy',
    description: 'Bring video + community insights up front.',
    sectionCatalogVersion: SECTION_CATALOG_VERSION,
    sections: [
      { id: 'introduction' },
      { id: 'video-insights' },
      { id: 'social-insights' },
      { id: 'quick-picks' },
      { id: 'products' },
      { id: 'what-we-looked-for' },
      { id: 'how-to-choose' },
      { id: 'evidence-snapshot' },
      { id: 'our-recommendation' },
      { id: 'final-recommendations' },
      { id: 'sources' },
    ],
  },
  {
    id: 'minimal',
    label: 'Minimal',
    description:
      'Skip optional sections; focus on picks + products + buying guide.',
    sectionCatalogVersion: SECTION_CATALOG_VERSION,
    sections: [
      { id: 'introduction' },
      { id: 'what-we-looked-for' },
      { id: 'quick-picks' },
      { id: 'products' },
      { id: 'how-to-choose' },
      { id: 'evidence-snapshot' },
      { id: 'our-recommendation' },
      { id: 'final-recommendations' },
      { id: 'sources' },
    ],
  },
]

// Validate at module load time so we fail fast during builds/tests.
for (const template of OUTLINE_TEMPLATES) {
  OutlineTemplateSchema.parse(template)
}

export function getOutlineTemplate(id: string): OutlineTemplate | undefined {
  const templateId = id as OutlineTemplateId
  return OUTLINE_TEMPLATES.find((template) => template.id === templateId)
}

export function resolveOutlineSections(
  templateId: OutlineTemplateId
): OutlineSection[] {
  const template = getOutlineTemplate(templateId)
  if (!template) return []

  const seen = new Set<string>()
  return template.sections.map(resolveSection).filter((section) => {
    if (!section.id) return false
    if (seen.has(section.id)) return false
    seen.add(section.id)
    return true
  })
}

export function listOutlineTemplateIds(): OutlineTemplateId[] {
  return [...OutlineTemplateIdSchema.options]
}

export function listOutlineTemplates(): OutlineTemplate[] {
  return [...OUTLINE_TEMPLATES]
}

export function isSectionInTemplate(
  templateId: OutlineTemplateId,
  sectionId: ReportSectionId
): boolean {
  const sections = resolveOutlineSections(templateId)
  return sections.some((section) => section.id === sectionId)
}
